import { use, useEffect, useState } from 'react';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';

import {
  ReadSubscribableOrganisationUnitTreeResponse,
  SubscribableOrganisationUnit,
} from 'features/care-connect-settings/domain/models/subscribable-organisation-unit-tree-response';
import { useReadSubscribableOrganisationUnitTree } from 'features/care-connect-settings/domain/queries/subscribable-organisation-unit-tree-queries';
import useCallPlanDetails, { CallPlanDetails } from 'features/care-connect-settings/hooks/call-plan-details';
import usePersonalNumberRangeDetails, {
  PersonalNumberRangeDetails,
} from 'features/care-connect-settings/hooks/personal-number-range-details';

export type CareConnectSettings = {
  subscribableOrganisationUnitTreeData: ReadSubscribableOrganisationUnitTreeResponse | undefined;
  subscribableOrganisationUnitTreeIsSuccess: boolean;
  subscribableOrganisationUnitTreeIsLoading: boolean;
  selectedOrganisationUnit: SubscribableOrganisationUnit | undefined;
  selectedOrganisationUnitBreadcrumbs: string[];
  callPlanDetails: CallPlanDetails;
  personalNumberRangeDetails: PersonalNumberRangeDetails;
  selectOrganisationUnit: (organisationUnit: SubscribableOrganisationUnit) => void;
  deselectOrganisationUnit: () => void;
};

const useCareConnectSettings = (): CareConnectSettings => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { showDefaultConfirmationPopUp } = use(ConfirmationPopupProviderContext);

  const [selectedOrganisationUnit, setSelectedOrganisationUnit] = useState<SubscribableOrganisationUnit | undefined>(undefined);

  const {
    data: subscribableOrganisationUnitTreeData,
    isSuccess: subscribableOrganisationUnitTreeIsSuccess,
    isLoading: subscribableOrganisationUnitTreeIsLoading,
    error: subscribableOrganisationUnitTreeError,
  } = useReadSubscribableOrganisationUnitTree();

  useEffect(() => {
    if (subscribableOrganisationUnitTreeError) {
      setErrorMessage(subscribableOrganisationUnitTreeError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribableOrganisationUnitTreeError]);

  const callPlanDetails = useCallPlanDetails(selectedOrganisationUnit, subscribableOrganisationUnitTreeData?.organisationUnits);
  const personalNumberRangeDetails = usePersonalNumberRangeDetails(selectedOrganisationUnit?.isRoot ?? false);

  const selectOrganisationUnit = (organisationUnit: SubscribableOrganisationUnit): void => {
    showDefaultConfirmationPopUp({
      shouldShow: callPlanDetails.callPlanForm.formState.isDirty || personalNumberRangeDetails.shouldShowConfirmationPopup,
      action: () => {
        callPlanDetails.cancelEditing();
        personalNumberRangeDetails.cancelEditing();
        setSelectedOrganisationUnit((prev) => (prev?.id === organisationUnit.id ? undefined : organisationUnit));
      },
    });
  };

  const deselectOrganisationUnit = (): void => {
    setSelectedOrganisationUnit(undefined);
  };

  return {
    subscribableOrganisationUnitTreeData,
    subscribableOrganisationUnitTreeIsSuccess,
    subscribableOrganisationUnitTreeIsLoading,
    selectedOrganisationUnit,
    selectedOrganisationUnitBreadcrumbs: selectedOrganisationUnit?.breadCrumbs ?? [],
    callPlanDetails,
    personalNumberRangeDetails,
    selectOrganisationUnit,
    deselectOrganisationUnit,
  };
};

export default useCareConnectSettings;
